import FormattedMessage from 'I18n/components/FormattedMessage';
import { useQuery } from 'data-fetching-client';
import { Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import * as AssetUninstallableReason from 'marketplace-ui-assets-core/constants/AssetUninstallableReason';
import { useAssetInstallability } from 'marketplace-ui-assets-core/hooks/useAssetInstallability';
import * as PlanType from 'marketplace-ui-common/constants/pricings/PlanType';
import * as RequestState from 'marketplace-ui-common/requestStates/RequestState';
import { isFulfilled, isSucceeded } from 'marketplace-ui-common/requestStates/RequestStateUtils';
import { formatCentsToDollars } from 'marketplace-ui-common/utils/currency';
import StorefrontUsageTrackerContext from '../../context/StorefrontUsageTrackerContext';
import AssetDetailsPage from '../../details/components/AssetDetailsPage';
import ExpandableTagsSection from '../../details/components/ExpandableTagsSection';
import { GET_ASSET_CONTENTS } from '../../details/queries/assetContentsQuery';
import { getAppContextCoreType } from '../../selectors/app';
import { selectPortalScopes, selectUserScopes
// @ts-expect-error untyped dependency
} from '../../selectors/auth';
import { useModuleCategories } from '../hooks/useModuleCategories';
import ModuleContentsResponse from '../records/ModuleContentsResponse';
import ModuleRecommendations from './ModuleRecommendationsSection';
/* @ts-expect-error module is untyped */
import UsageViewTracker from '../../components/tracking/UsageViewTracker';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export default function ModuleDetailsPage({
  moduleDetails,
  use
}) {
  const isAdminPreview = use === 'admin-preview';
  const isPreview = use === 'preview' || isAdminPreview;
  const appContext = useSelector(getAppContextCoreType);
  const userScopes = useSelector(selectUserScopes);
  const portalScopes = useSelector(selectPortalScopes);
  const [requestState, setRequestState] = useState(RequestState.UNINITIALIZED);
  const {
    offering: {
      homemade
    },
    offeringId,
    pricing,
    provider: {
      name: providerName
    },
    s3BucketKey,
    slug,
    tags
  } = moduleDetails;
  const {
    assetUninstallableReason,
    requestState: assetInstallabilityRequestState
  } = useAssetInstallability({
    appContext,
    offeringId,
    portalScopes,
    userScopes
  });
  const {
    planType,
    cents
  } = pricing;
  const isFree = planType === PlanType.FREE;
  const screen = isAdminPreview ? 'details admin preview' : use === 'preview' ? 'details preview' : 'details';
  const getEventProperties = useCallback(() => {
    return {
      screen,
      slug,
      type: 'module',
      homemade,
      offeringId,
      orderTotal: isFree ? 0 : formatCentsToDollars(cents),
      isInstallableByUser: assetUninstallableReason !== AssetUninstallableReason.MISSING_MARKETPLACE_ASSET_ACCESS,
      isNotInstallableReason: assetUninstallableReason || undefined
    };
  }, [assetUninstallableReason, cents, homemade, isFree, offeringId, screen, slug]);

  /**
   * Usage tracking
   */
  const {
    setDefaultEventProperties,
    trackDetailsPageview,
    trackDetailsInteraction
  } = useContext(StorefrontUsageTrackerContext);

  // Set default event properties
  useEffect(() => {
    setDefaultEventProperties(getEventProperties());
  }, [getEventProperties, setDefaultEventProperties]);

  // Reset default event properties on unmount
  useEffect(() => () => setDefaultEventProperties(null), [setDefaultEventProperties]);
  useEffect(() => {
    if (isFulfilled(assetInstallabilityRequestState)) {
      setRequestState(RequestState.SUCCEEDED);
    }
  }, [assetInstallabilityRequestState]);
  const {
    loading,
    error,
    data,
    previousData
  } = useQuery(GET_ASSET_CONTENTS, {
    variables: {
      bucketKey: s3BucketKey || ''
    }
  });
  const moduleContentsRequestState = useMemo(() => {
    if (loading) {
      return RequestState.PENDING;
    } else if (error) {
      return RequestState.FAILED;
    } else if (data || previousData) {
      return RequestState.SUCCEEDED;
    } else {
      return RequestState.UNINITIALIZED;
    }
  }, [loading, data, previousData, error]);
  const moduleContentsData = useMemo(() => {
    const contentsData = data || previousData;
    if (!contentsData) {
      return new ModuleContentsResponse();
    } else {
      return new ModuleContentsResponse(contentsData.contents);
    }
  }, [data, previousData]);
  const categories = useMemo(() => {
    return moduleContentsData.contents.reduce((final, contentItem) => {
      return [/* eslint-disable-next-line hubspot-dev/no-reduce-accumulator-copy */
      ...final, ...contentItem.categories];
    }, []).map(category => category.toUpperCase()); // TODO: work with CPG to normalize categories
  }, [moduleContentsData]);
  const {
    requestState: moduleCategoriesRequestState,
    getCategoryDisplayName
  } = useModuleCategories();
  const categoryTranslations = useMemo(() => categories.map(category => getCategoryDisplayName(category)), [categories, getCategoryDisplayName]);
  const renderedExpandableTagsSection = useMemo(() => /*#__PURE__*/_jsxs(Fragment, {
    children: [categories.length > 0 && isSucceeded(moduleCategoriesRequestState) && /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx("dt", {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-assets-ui.modules.details.moduleInfo.categories"
        })
      }), /*#__PURE__*/_jsx("dd", {
        children: /*#__PURE__*/_jsx(ExpandableTagsSection, {
          getTagText: category => getCategoryDisplayName(category),
          isPreview: isPreview,
          onTrackInteraction: trackDetailsInteraction,
          queryField: "categories",
          tags: categories
        })
      })]
    }), tags.length > 0 && /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx("dt", {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-assets-ui.modules.details.moduleInfo.searchTags"
        })
      }), /*#__PURE__*/_jsx("dd", {
        children: /*#__PURE__*/_jsx(ExpandableTagsSection, {
          isPreview: isPreview,
          onTrackInteraction: trackDetailsInteraction,
          queryField: "tags",
          tags: tags
        })
      })]
    })]
  }), [categories, getCategoryDisplayName, moduleCategoriesRequestState, isPreview, tags, trackDetailsInteraction]);
  const renderedAssetRecommendationsSection = useMemo(() => /*#__PURE__*/_jsx(ModuleRecommendations, {
    categories: categories,
    categoryTranslations: categoryTranslations,
    isPreview: isPreview,
    offeringId: offeringId,
    onTrackInteraction: trackDetailsInteraction,
    onTrackPageview: trackDetailsPageview,
    providerName: providerName
  }), [categories, categoryTranslations, isPreview, offeringId, providerName, trackDetailsInteraction, trackDetailsPageview]);
  return /*#__PURE__*/_jsxs(NavMarker, {
    name: "MODULE_DETAILS_LOAD",
    children: [/*#__PURE__*/_jsx(AssetDetailsPage, {
      assetContentsRequestState: moduleContentsRequestState,
      assetDetails: moduleDetails,
      assetScopes: moduleContentsData.scopes,
      offeringType: "MODULE",
      renderedAssetRecommendationsSection: renderedAssetRecommendationsSection,
      renderedExpandableTagsSection: renderedExpandableTagsSection,
      use: use
    }), /*#__PURE__*/_jsx(UsageViewTracker, {
      active: isFulfilled(requestState),
      onTrackView: trackDetailsPageview
    })]
  });
}